import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Check, Loader2 } from 'lucide-react'

interface SubscriptionStepProps {
  onSubmit: (data: Record<string, unknown>) => void
  disabled?: boolean
}

const PLANS = [
  {
    tier: 'free',
    name: 'Free',
    description: 'Perfect for getting started',
    priceMonthly: 0,
    priceAnnual: 0,
    features: [
      '5 templates',
      '1 AI agent',
      '100 AI generations/month',
      'Basic support'
    ],
    popular: false
  },
  {
    tier: 'pro',
    name: 'Pro',
    description: 'For power users and small teams',
    priceMonthly: 29,
    priceAnnual: 290,
    features: [
      'Unlimited templates',
      '6 AI agents',
      '1,000 AI generations/month',
      'Advanced integrations',
      'Priority support'
    ],
    popular: true
  },
  {
    tier: 'enterprise',
    name: 'Enterprise',
    description: 'For large organizations',
    priceMonthly: 99,
    priceAnnual: 990,
    features: [
      'Everything in Pro',
      'Unlimited AI agents',
      'Unlimited AI generations',
      'SSO & SAML',
      'Custom integrations',
      'Dedicated support'
    ],
    popular: false
  }
]

export function SubscriptionStep({ onSubmit, disabled }: SubscriptionStepProps) {
  const [selectedTier, setSelectedTier] = useState('pro')
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual')
  const [trialEnabled, setTrialEnabled] = useState(true)

  const handleSubmit = () => {
    onSubmit({
      tier: selectedTier,
      billingCycle,
      trialEnabled
    })
  }

  const selectedPlan = PLANS.find(p => p.tier === selectedTier)

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-slate-900 mb-2">
          Choose your plan
        </h2>
        <p className="text-slate-600">
          Start with a 14-day free trial. No credit card required.
        </p>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-4">
        <span className={billingCycle === 'monthly' ? 'font-medium' : 'text-slate-500'}>
          Monthly
        </span>
        <Switch
          checked={billingCycle === 'annual'}
          onCheckedChange={(checked) => setBillingCycle(checked ? 'annual' : 'monthly')}
        />
        <span className={billingCycle === 'annual' ? 'font-medium' : 'text-slate-500'}>
          Annual
          <Badge variant="secondary" className="ml-2">Save 17%</Badge>
        </span>
      </div>

      {/* Plans grid */}
      <div className="grid gap-4 sm:grid-cols-3">
        {PLANS.map((plan) => {
          const price = billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly * 12
          const monthlyPrice = billingCycle === 'annual' ? Math.round(plan.priceAnnual / 12) : plan.priceMonthly

          return (
            <Card
              key={plan.tier}
              className={`cursor-pointer transition-all hover:shadow-md relative ${
                selectedTier === plan.tier ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => setSelectedTier(plan.tier)}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                  Most Popular
                </Badge>
              )}
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-4">
                  <span className="text-3xl font-bold">
                    ${monthlyPrice}
                  </span>
                  <span className="text-slate-500">/month</span>
                  {billingCycle === 'annual' && plan.priceAnnual > 0 && (
                    <p className="text-sm text-slate-500">
                      ${price} billed annually
                    </p>
                  )}
                </div>
                <ul className="space-y-2 text-left">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="text-sm flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Trial toggle */}
      {selectedPlan && selectedPlan.priceMonthly > 0 && (
        <div className="max-w-md mx-auto pt-4 border-t">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="trial" className="font-medium">
                Start with 14-day free trial
              </Label>
              <p className="text-sm text-slate-500">
                No credit card required
              </p>
            </div>
            <Switch
              id="trial"
              checked={trialEnabled}
              onCheckedChange={setTrialEnabled}
            />
          </div>
        </div>
      )}

      <div className="flex justify-center pt-4">
        <Button onClick={handleSubmit} disabled={disabled} size="lg">
          {disabled ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : selectedPlan?.priceMonthly === 0 ? (
            'Start Free'
          ) : trialEnabled ? (
            'Start Free Trial'
          ) : (
            `Continue with ${selectedPlan?.name}`
          )}
        </Button>
      </div>
    </div>
  )
}
